/*
 *  Broker work schedule — a Mon–Sun week grid of who works which days.
 *
 *  Rows are the brokers the viewer can manage/see (same roster as the
 *  Team benchmarks); columns are the seven days. Each cell shows the
 *  resolved state — working, or off with a reason — and where it came
 *  from (a date exception, the recurring weekly pattern, or the opt-out
 *  default). Clicking a cell toggles it; a per-row modal edits the
 *  recurring weekly pattern; a header modal books a vacation range.
 *
 *  All state flows from GET /api/schedule/week; every mutation re-fetches
 *  via reload() so the grid always reflects the server's resolution.
 */
import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/Skeleton";
import type {
  BrokerWeek,
  ScheduleAuditEntry,
  ScheduleDay,
  ScheduleOffReason,
  ScheduleWeek,
} from "@/types";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const REASON_LABELS: Record<ScheduleOffReason, string> = {
  day_off: "Day off",
  vacation: "Vacation",
  sick: "Sick",
  holiday: "Holiday",
  other: "Other",
};

const REASON_OPTIONS: ScheduleOffReason[] = [
  "day_off",
  "vacation",
  "sick",
  "holiday",
  "other",
];

// Pill tone per off-reason — keeps the grid scannable (vacation reads
// distinct from a routine day off at a glance).
const REASON_STYLES: Record<ScheduleOffReason, string> = {
  day_off: "border-slate-500/30 bg-slate-500/10 text-slate-300",
  vacation: "border-sky-500/30 bg-sky-500/10 text-sky-300",
  sick: "border-rose-500/30 bg-rose-500/10 text-rose-300",
  holiday: "border-violet-500/30 bg-violet-500/10 text-violet-300",
  other: "border-amber-500/30 bg-amber-500/10 text-amber-300",
};

function dayNumber(iso: string): string {
  const [, , dd] = iso.split("-");
  return String(Number(dd));
}

function formatWeekRange(w: ScheduleWeek): string {
  const fmt = (iso: string) => {
    const [y, m, dd] = iso.split("-").map(Number);
    return new Date(y, m - 1, dd).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  };
  const tag = w.is_current_week ? " · this week" : "";
  return `${fmt(w.week_start)} – ${fmt(w.week_end)}${tag}`;
}

export function Schedule() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [week, setWeek] = useState<ScheduleWeek | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [patternFor, setPatternFor] = useState<BrokerWeek | null>(null);
  const [offDayFor, setOffDayFor] = useState<{
    broker: BrokerWeek;
    day: ScheduleDay;
  } | null>(null);
  const [timeOffOpen, setTimeOffOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState<BrokerWeek | null>(null);

  async function reload() {
    try {
      const w = await api<ScheduleWeek>(
        `/api/schedule/week?week_offset=${weekOffset}`,
      );
      setWeek(w);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load schedule");
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset]);

  // Toggle one cell. Working → ask for a reason (off-day modal). Off via
  // an exception → delete it (revert to pattern/default). Off via the
  // pattern → force the day on with an exception.
  async function toggleDay(broker: BrokerWeek, day: ScheduleDay) {
    if (!broker.can_manage) return;
    try {
      if (day.is_working) {
        setOffDayFor({ broker, day });
        return;
      }
      if (day.source === "exception") {
        await api(
          `/api/schedule/users/${broker.user_id}/exceptions?start_date=${day.date}`,
          { method: "DELETE" },
        );
      } else {
        await api(`/api/schedule/users/${broker.user_id}/exceptions`, {
          method: "POST",
          json: { start_date: day.date, is_working: true },
        });
      }
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to update day");
    }
  }

  const manageableBrokers = week?.brokers.filter((b) => b.can_manage) ?? [];

  return (
    <div className="w-full">
      <PageHeader
        title="Schedule"
        subtitle="Who works which days. Days with no entry count as working."
        actions={
          manageableBrokers.length > 0 ? (
            <button className="btn-primary" onClick={() => setTimeOffOpen(true)}>
              Add time off
            </button>
          ) : null
        }
      />

      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="text-sm text-slate-400">
          {week ? formatWeekRange(week) : "Loading…"}
        </div>
        <div className="inline-flex bg-ink-900/60 border border-white/10 rounded-lg p-0.5">
          <button
            type="button"
            onClick={() => setWeekOffset((o) => Math.max(-52, o - 1))}
            className="px-2.5 py-1 text-[11.5px] text-slate-400 hover:text-slate-200 rounded disabled:opacity-40"
            disabled={weekOffset <= -52}
          >
            ← Prev
          </button>
          <button
            type="button"
            onClick={() => setWeekOffset(0)}
            className={`px-2.5 py-1 rounded text-[11.5px] transition ${
              weekOffset === 0
                ? "bg-accent-500/20 text-accent-200"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            This week
          </button>
          <button
            type="button"
            onClick={() => setWeekOffset((o) => Math.min(0, o + 1))}
            className="px-2.5 py-1 text-[11.5px] text-slate-400 hover:text-slate-200 rounded disabled:opacity-40"
            disabled={weekOffset >= 0}
          >
            Next →
          </button>
        </div>
      </div>

      {error && (
        <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm mb-4">
          {error}
        </div>
      )}

      {/* Desktop grid */}
      <div className="card overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/5">
            <tr>
              <th className="text-left px-4 py-3 w-56">Broker</th>
              {(week?.brokers[0]?.days ?? WEEKDAY_LABELS.map(() => null)).map(
                (d, i) => (
                  <th key={i} className="px-2 py-3 text-center">
                    <div>{WEEKDAY_LABELS[i]}</div>
                    {d && (
                      <div className="text-[10px] text-slate-600 font-normal">
                        {dayNumber(d.date)}
                      </div>
                    )}
                  </th>
                ),
              )}
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {week === null &&
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="px-4 py-3" colSpan={9}>
                    <Skeleton className="h-8" />
                  </td>
                </tr>
              ))}
            {week?.brokers.length === 0 && (
              <tr>
                <td
                  className="px-4 py-6 text-center text-slate-500 text-sm"
                  colSpan={9}
                >
                  No brokers to schedule yet.
                </td>
              </tr>
            )}
            {week?.brokers.map((b) => (
              <tr
                key={b.user_id}
                className="border-t border-white/5 hover:bg-white/[0.02]"
              >
                <td className="px-4 py-2">
                  <Link
                    to={`/team/${b.user_id}`}
                    className="flex items-center gap-2 group/name"
                  >
                    <Avatar
                      url={b.avatar_url}
                      name={b.full_name}
                      email={b.email}
                      size="sm"
                    />
                    <span className="truncate text-slate-200 group-hover/name:text-white">
                      {b.full_name || b.email || "—"}
                    </span>
                  </Link>
                </td>
                {b.days.map((d) => (
                  <td key={d.date} className="px-2 py-2 text-center">
                    <DayCell
                      day={d}
                      canManage={b.can_manage}
                      onClick={() => toggleDay(b, d)}
                    />
                  </td>
                ))}
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button
                    onClick={() => setHistoryFor(b)}
                    className="text-[11px] text-slate-400 hover:text-slate-200 px-2 py-1 rounded hover:bg-white/5"
                    title="View change history"
                  >
                    History
                  </button>
                  {b.can_manage && (
                    <button
                      onClick={() => setPatternFor(b)}
                      className="text-[11px] text-slate-400 hover:text-slate-200 px-2 py-1 rounded hover:bg-white/5"
                      title="Edit recurring weekly pattern"
                    >
                      Pattern
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {week === null &&
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card p-3">
              <Skeleton className="h-16" />
            </div>
          ))}
        {week?.brokers.length === 0 && (
          <div className="card p-6 text-center text-slate-500 text-sm">
            No brokers to schedule yet.
          </div>
        )}
        {week?.brokers.map((b) => (
          <div key={b.user_id} className="card p-3">
            <div className="flex items-center justify-between mb-2">
              <Link
                to={`/team/${b.user_id}`}
                className="flex items-center gap-2 min-w-0"
              >
                <Avatar
                  url={b.avatar_url}
                  name={b.full_name}
                  email={b.email}
                  size="sm"
                />
                <span className="truncate text-slate-200">
                  {b.full_name || b.email || "—"}
                </span>
              </Link>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setHistoryFor(b)}
                  className="text-[11px] text-slate-400 hover:text-slate-200 px-2 py-1 rounded hover:bg-white/5"
                >
                  History
                </button>
                {b.can_manage && (
                  <button
                    onClick={() => setPatternFor(b)}
                    className="text-[11px] text-slate-400 hover:text-slate-200 px-2 py-1 rounded hover:bg-white/5"
                  >
                    Pattern
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {b.days.map((d, i) => (
                <div key={d.date} className="text-center">
                  <div className="text-[9px] text-slate-600 mb-0.5">
                    {WEEKDAY_LABELS[i]}
                  </div>
                  <DayCell
                    day={d}
                    canManage={b.can_manage}
                    onClick={() => toggleDay(b, d)}
                    compact
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {patternFor && (
        <PatternModal
          broker={patternFor}
          onClose={() => setPatternFor(null)}
          onSaved={async () => {
            setPatternFor(null);
            await reload();
          }}
        />
      )}
      {offDayFor && (
        <OffDayModal
          broker={offDayFor.broker}
          day={offDayFor.day}
          defaultReason={offDayFor.broker.off_reason ?? "day_off"}
          onClose={() => setOffDayFor(null)}
          onSaved={async () => {
            setOffDayFor(null);
            await reload();
          }}
        />
      )}
      {timeOffOpen && (
        <TimeOffModal
          brokers={manageableBrokers}
          onClose={() => setTimeOffOpen(false)}
          onSaved={async () => {
            setTimeOffOpen(false);
            await reload();
          }}
        />
      )}
      {historyFor && (
        <HistoryModal
          broker={historyFor}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </div>
  );
}

function DayCell({
  day,
  canManage,
  onClick,
  compact,
}: {
  day: ScheduleDay;
  canManage: boolean;
  onClick: () => void;
  compact?: boolean;
}) {
  const base = compact
    ? "w-full h-7 rounded text-[10px] flex items-center justify-center border"
    : "w-16 h-8 rounded-md text-[11px] inline-flex items-center justify-center border";
  const cursor = canManage ? "cursor-pointer" : "cursor-default";
  // Default-working (no schedule data) renders faint; pattern/exception
  // render solid so the eye can tell declared state from the fallback.
  const faint = day.source === "default";

  if (day.is_working) {
    return (
      <button
        type="button"
        disabled={!canManage}
        onClick={onClick}
        title={day.source === "default" ? "Working (default)" : "Working"}
        className={`${base} ${cursor} ${
          faint
            ? "border-white/5 bg-emerald-500/[0.04] text-emerald-300/40 hover:bg-emerald-500/10"
            : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
        }`}
      >
        On
      </button>
    );
  }
  const reason = (day.off_reason ?? "day_off") as ScheduleOffReason;
  return (
    <button
      type="button"
      disabled={!canManage}
      onClick={onClick}
      title={day.note || REASON_LABELS[reason]}
      className={`${base} ${cursor} ${REASON_STYLES[reason]} hover:brightness-125`}
    >
      {compact ? "·" : REASON_LABELS[reason]}
    </button>
  );
}

function ModalShell({
  title,
  subtitle,
  children,
  onClose,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="card max-w-md w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="card-title">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}

function PatternModal({
  broker,
  onClose,
  onSaved,
}: {
  broker: BrokerWeek;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [weekdays, setWeekdays] = useState<Set<number>>(
    () => new Set(broker.working_weekdays ?? [1, 2, 3, 4, 5]),
  );
  const [offReason, setOffReason] = useState<ScheduleOffReason>(
    broker.off_reason ?? "day_off",
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggle(iso: number) {
    setWeekdays((prev) => {
      const next = new Set(prev);
      if (next.has(iso)) next.delete(iso);
      else next.add(iso);
      return next;
    });
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/schedule/users/${broker.user_id}/pattern`, {
        method: "PUT",
        json: {
          working_weekdays: [...weekdays].sort(),
          off_reason: offReason,
        },
      });
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to save pattern");
    } finally {
      setBusy(false);
    }
  }

  async function clearPattern() {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/schedule/users/${broker.user_id}/pattern`, {
        method: "DELETE",
      });
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to clear pattern");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      title="Weekly pattern"
      subtitle={`${broker.full_name || broker.email} · pick the days they normally work.`}
      onClose={onClose}
    >
      <div className="flex flex-wrap gap-2">
        {WEEKDAY_LABELS.map((label, i) => {
          const iso = i + 1; // Mon=1 … Sun=7
          const on = weekdays.has(iso);
          return (
            <button
              key={iso}
              type="button"
              onClick={() => toggle(iso)}
              className={`px-3 py-1.5 rounded-md text-xs border transition ${
                on
                  ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-200"
                  : "border-white/10 bg-white/[0.02] text-slate-500 hover:text-slate-300"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div>
        <div className="label">Off-day reason</div>
        <select
          className="input"
          value={offReason}
          onChange={(e) => setOffReason(e.target.value as ScheduleOffReason)}
        >
          {REASON_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {REASON_LABELS[r]}
            </option>
          ))}
        </select>
      </div>

      {err && (
        <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
          {err}
        </div>
      )}

      <div className="flex justify-between items-center pt-2">
        <button
          type="button"
          onClick={clearPattern}
          disabled={busy || !broker.has_pattern}
          className="text-[11px] text-slate-400 hover:text-rose-300 disabled:opacity-40"
        >
          Clear pattern (use default)
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={save}
            disabled={busy}
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function OffDayModal({
  broker,
  day,
  defaultReason,
  onClose,
  onSaved,
}: {
  broker: BrokerWeek;
  day: ScheduleDay;
  defaultReason: ScheduleOffReason;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [reason, setReason] = useState<ScheduleOffReason>(defaultReason);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/schedule/users/${broker.user_id}/exceptions`, {
        method: "POST",
        json: {
          start_date: day.date,
          is_working: false,
          off_reason: reason,
          note: note.trim() || null,
        },
      });
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to mark day off");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      title="Mark day off"
      subtitle={`${broker.full_name || broker.email} · ${day.date}`}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-3">
        <div>
          <div className="label">Reason</div>
          <select
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value as ScheduleOffReason)}
          >
            {REASON_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {REASON_LABELS[r]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <div className="label">Note (optional)</div>
          <input
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. doctor's appointment"
          />
        </div>
        {err && (
          <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
            {err}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? "Saving…" : "Mark off"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function TimeOffModal({
  brokers,
  onClose,
  onSaved,
}: {
  brokers: BrokerWeek[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [userId, setUserId] = useState(brokers[0]?.user_id ?? "");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState<ScheduleOffReason>("vacation");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/schedule/users/${userId}/exceptions`, {
        method: "POST",
        json: {
          start_date: startDate,
          end_date: endDate || null,
          is_working: false,
          off_reason: reason,
          note: note.trim() || null,
        },
      });
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to add time off");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      title="Add time off"
      subtitle="Block a range of days off for a broker (vacation, sick leave, etc.)."
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-3">
        <div>
          <div className="label">Broker</div>
          <select
            className="input"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          >
            {brokers.map((b) => (
              <option key={b.user_id} value={b.user_id}>
                {b.full_name || b.email}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="label">From</div>
            <input
              className="input"
              type="date"
              required
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <div className="label">To (optional)</div>
            <input
              className="input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
        <div>
          <div className="label">Reason</div>
          <select
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value as ScheduleOffReason)}
          >
            {REASON_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {REASON_LABELS[r]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <div className="label">Note (optional)</div>
          <input
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. annual leave"
          />
        </div>
        {err && (
          <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
            {err}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={busy || !userId || !startDate}
          >
            {busy ? "Saving…" : "Add time off"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

const ACTION_LABELS: Record<ScheduleAuditEntry["action"], string> = {
  pattern_set: "Set weekly pattern",
  pattern_cleared: "Cleared weekly pattern",
  exception_set: "Marked days",
  exception_cleared: "Cleared days",
};

function weekdayNames(days: unknown): string {
  if (!Array.isArray(days)) return "—";
  return (days as number[])
    .map((d) => WEEKDAY_LABELS[d - 1] ?? String(d))
    .join(", ");
}

// Human one-liner for an audit entry's detail payload.
function summarize(e: ScheduleAuditEntry): string {
  const d = e.detail || {};
  if (e.action === "pattern_set") {
    const after = (d.after as Record<string, unknown>) || {};
    return `Working days → ${weekdayNames(after.working_weekdays)}`;
  }
  if (e.action === "pattern_cleared") {
    return "Reverted to default (all days working)";
  }
  if (e.action === "exception_set") {
    const range =
      d.start_date === d.end_date
        ? String(d.start_date)
        : `${d.start_date} → ${d.end_date}`;
    const state = d.is_working
      ? "working"
      : `off (${REASON_LABELS[(d.off_reason as ScheduleOffReason) ?? "day_off"]})`;
    const note = d.note ? ` · ${d.note}` : "";
    return `${range}: ${state}${note}`;
  }
  if (e.action === "exception_cleared") {
    const range =
      d.start_date === d.end_date
        ? String(d.start_date)
        : `${d.start_date} → ${d.end_date}`;
    return `${range}: removed ${d.days ?? ""} day override${d.days === 1 ? "" : "s"}`;
  }
  return "";
}

function HistoryModal({
  broker,
  onClose,
}: {
  broker: BrokerWeek;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<ScheduleAuditEntry[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api<ScheduleAuditEntry[]>(
          `/api/schedule/users/${broker.user_id}/history?limit=100`,
        );
        setRows(r);
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : "Failed to load history");
      }
    })();
  }, [broker.user_id]);

  return (
    <ModalShell
      title="Change history"
      subtitle={`${broker.full_name || broker.email} · who changed what, newest first`}
      onClose={onClose}
    >
      {err && (
        <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
          {err}
        </div>
      )}
      <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
        {rows === null && <Skeleton className="h-24" />}
        {rows?.length === 0 && (
          <div className="text-sm text-slate-500 py-6 text-center">
            No changes recorded yet.
          </div>
        )}
        <ul className="space-y-2">
          {rows?.map((e) => (
            <li
              key={e.id}
              className="border border-white/5 rounded-md p-2.5 bg-white/[0.02]"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] font-medium text-slate-200">
                  {ACTION_LABELS[e.action]}
                </span>
                <span className="text-[10.5px] text-slate-500 tabular-nums">
                  {new Date(e.created_at).toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </span>
              </div>
              <div className="text-[11.5px] text-slate-400 mt-0.5">
                {summarize(e)}
              </div>
              <div className="text-[10.5px] text-slate-600 mt-0.5">
                by {e.actor_name || "—"}
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex justify-end pt-2">
        <button type="button" className="btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>
    </ModalShell>
  );
}
